import {
    ComponentServices,
    fetchSubscriptionOffers,
    getFeatureValue,
    getSubscriptionOfferPublicationId,
    OfferGroup,
} from '@news-mono/web-common'
import {
    FeatureState,
    isFeatureEnabled,
    toFeatureState,
} from '@west-australian-newspapers/etrigan-feature-toggles-client'
import { DataDefinition } from '@west-australian-newspapers/json-react-layouts-data-loader'
import {
    GetOffersResult,
    OfferVariantType,
} from '@west-australian-newspapers/subscription-offer-types'
import { getRelatedPublicationListing } from '../../templates/Publication/lib/get-related-publication-listing'
import {
    DEFAULT_BREACH_TYPE,
    getBreachWallOptions,
} from '../../user-registration/breach/helpers'
import { RelatedPublications } from './Publication.props'

export interface PublicationDataContent {
    publicationId: string
    /** Only set by mastheads served by the subscription offers API (The West and its regionals) */
    subscriptionOffers?: {
        offerGroup: OfferGroup
    }
}

export interface PublicationDataResult {
    relatedPublications?: RelatedPublications
    subscriptionOffers?: GetOffersResult
}

export type ArticlePublicationDataDefinition = DataDefinition<
    PublicationDataContent,
    PublicationDataResult,
    ComponentServices
>

/** Resolves the same variant BreachWallContentWrapper uses, otherwise the seeded query cache is missed */
const loadBreachSubscriptionOffers = async (
    offerGroup: OfferGroup,
    featureState: FeatureState,
    services: ComponentServices,
): Promise<GetOffersResult | undefined> => {
    const campaignOffer =
        (getFeatureValue(
            'campaign-offer',
            featureState,
            DEFAULT_BREACH_TYPE,
        ) as OfferVariantType) ?? DEFAULT_BREACH_TYPE
    const { type: variant } = getBreachWallOptions(
        services.location,
        campaignOffer,
    )

    try {
        return await fetchSubscriptionOffers(
            getSubscriptionOfferPublicationId(services.config.publicHostname),
            services.log,
            offerGroup,
            variant,
            { subscriptionOfferApi: services.config.subscriptionOfferApi },
        )
    } catch (error) {
        // The breach wall refetches on the client, so a failure here shouldn't fail the article
        services.log.error(
            { error, offerGroup, variant },
            'Failed to preload subscription offers for publication',
        )
        return undefined
    }
}

export const ArticlePublicationDataDefinitionLoader: ArticlePublicationDataDefinition =
    {
        loadData: async (props, services) => {
            const appState = services.store.getState()
            const featureState = toFeatureState(appState.toggles)

            const [relatedPublications, subscriptionOffers] = await Promise.all(
                [
                    isFeatureEnabled(featureState, 'related-content')
                        ? getRelatedPublicationListing(
                              services,
                              props.publicationId,
                          )
                        : undefined,
                    props.subscriptionOffers
                        ? loadBreachSubscriptionOffers(
                              props.subscriptionOffers.offerGroup,
                              featureState,
                              services,
                          )
                        : undefined,
                ],
            )

            return { relatedPublications, subscriptionOffers }
        },
    }
